import { Card, Button, Tag, MenuItem, Select } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../api/axiosInstance';
import { useLoader } from '../../hooks/useLoader';
import { useTheme } from '../../hooks/useTheme';
import styles from './NewAutomationFlow.module.css';

// Frontend UI cards - these are just presentation layer
const cardGroups = [
  {
    groupTitle: 'Cloud RAN Workflow Configuration Templates',
    productType: 'CR gNb (Cloud RAN)',
    cards: [
      {
        title: 'Cloud RAN Workflow',
        wizardTitle: 'Cloud RAN Workflow',
        subtitle: 'Configurable automation workflow for gNB products and Cloud RAN Products.',
        tier: 'Standard',
        label: '',
        description: '',
        backendTemplate: 'Full_Automation',
        artifactTypeRule: '',
        category: 'all'
      }
    ]
  },
  {
    groupTitle: 'rApps Workflow Configuration Templates',
    productType: 'rApps',
    cards: [
      {
        title: 'rApps Workflow',
        wizardTitle: 'rApps Workflow',
        subtitle: 'Configure your automation workflow for rApps products.',
        tier: 'Standard',
        label: '',
        backendTemplate: 'Full_Automation',
        artifactTypeRule: '',
        category: 'all'
      }
    ]
  },
  {
    groupTitle: 'Purpose Built Configuration Templates',
    productType: 'Purpose Built',
    cards: [
      {
        title: 'Purpose Built Workflow',
        wizardTitle: 'Purpose Built Workflow',
        subtitle: 'Configurable automation workflow for Purpose Built products.',
        tier: 'Standard',
        label: '',
        description: '',
        backendTemplate: 'Full_Automation',
        artifactTypeRule: '',
        category: 'all'
      }
    ]
  },
  {
    groupTitle: 'CNIS Workflow Configuration Templates',
    productType: 'CNIS',
    cards: [
      {
        title: 'CNIS Workflow',
        wizardTitle: 'CNIS Workflow',
        subtitle: 'Specialized onboarding flow for CNIS products.',
        tier: 'Standard',
        label: '',
        description: '',
        backendTemplate: 'Onboarding_Only',
        artifactTypeRule: 'unverified',
        category: 'Onboarding'
      }
    ]
  }
];

interface TemplateStage {
  id: string;
  name: string;
  order: number;
}

type CardWithProductType = (typeof cardGroups)[number]['cards'][number] & { productType: string };

const filterOptions = [...new Set(cardGroups.map((g) => g.productType))];
/**
 * New Automation Flow page
 * Template selection organized by categories
 */
function NewAutomationFlow() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [cardStages, setCardStages] = useState<Record<string, TemplateStage[]>>({});
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>(filterOptions);

  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    const uniqueTemplates = [...new Set(cardGroups.flatMap((g) => g.cards.map((c) => c.backendTemplate)))];
    showLoader();
    api
      .get(`/api/templates/stages?names=${uniqueTemplates.join(',')}`)
      .then((response) => {
        const stagesMap = response.data.reduce(
          (acc: Record<string, TemplateStage[]>, item: { templateName: string; stages: TemplateStage[] }) => {
            acc[item.templateName] = item.stages;
            return acc;
          },
          {} as Record<string, TemplateStage[]>
        );
        setCardStages(stagesMap);
      })
      .catch((error) => console.error('Failed to load stages:', error))
      .finally(() =>
        setTimeout(() => {
          hideLoader();
        }, 500)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateClick = (card: CardWithProductType) => {
    navigate('/automation-flows/new/wizard', {
      state: {
        selectedCard: card,
        selectedTemplate: card.backendTemplate,
        preSelectedProductType: card.productType,
        artifactTypeRule: card.artifactTypeRule
      }
    });
  };
  const getTemplateStages = (stages: TemplateStage[], backendTemplate: string, artifactTypeRule: string): TemplateStage[] => {
    if (backendTemplate === 'Onboarding_Only' && artifactTypeRule === 'verified') {
      return stages.filter((s) => s.name.toLowerCase().includes('artifacts'));
    }
    return stages;
  };

  return (
    <div className="row">
      <div className="column sm-12">
        <div className={theme ? 'light-theme' : 'dark-theme'}>
          <div
            className={`${styles.settingsContainer} ${styles.settingsContainerPadding}  ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
          >
            <div className={styles.settingsTitleContainer}>
              <div className={styles.settingsTitle}>Create Configuration Instance</div>
              <p className={styles.subtitle}>
                Choose a workflow configuration template to start creating a configuration instance for your Automation Workflows.
              </p>
              <div className={styles.filterSection}>
                <div className={styles.filterLabel}>
                  Filter by <span className={styles.filterCount}>{selectedProductTypes.length} Filters applied</span>
                </div>
                <div>
                  <ReactEDSWrapper
                    component={Select}
                    className={styles.select}
                    type="input-multi"
                    label="Select option"
                    value={selectedProductTypes}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(selected: any) => setSelectedProductTypes(selected)}
                  >
                    {filterOptions.map((option, index) => (
                      <ReactEDSWrapper component={MenuItem} key={index} value={option}>
                        {option}
                      </ReactEDSWrapper>
                    ))}
                  </ReactEDSWrapper>
                </div>
              </div>
              <div className={styles.cardGrid}>
                {cardGroups
                  .filter((group) => selectedProductTypes.length === 0 || selectedProductTypes.includes(group.productType))
                  .map((group) =>
                    group.cards.map((card) => (
                      <div key={card.title} className={styles.cardWrapper}>
                        <ReactEDSWrapper component={Card} title={card.title} subtitle={card.subtitle || card.tier}>
                          <div className={styles.card}>
                            {card.label && (
                              <div className={styles.tagWrapper}>
                                <ReactEDSWrapper component={Tag} color={card.label === 'Verified' ? 'green' : 'blue'} big>
                                  {card.label}
                                </ReactEDSWrapper>
                              </div>
                            )}
                            <div className={styles.stages}>
                              {card.backendTemplate && cardStages[card.backendTemplate] ? (
                                getTemplateStages(cardStages[card.backendTemplate], card.backendTemplate, card.artifactTypeRule).map(
                                  (stage, i) => (
                                    <ReactEDSWrapper key={i} component={Tag} big>
                                      <span>{stage.name}</span>
                                    </ReactEDSWrapper>
                                  )
                                )
                              ) : card.backendTemplate ? (
                                <ReactEDSWrapper component={Tag} big>
                                  <span>Loading...</span>
                                </ReactEDSWrapper>
                              ) : null}
                            </div>
                            <ReactEDSWrapper
                              component={Button}
                              className={styles.button}
                              onClick={() => handleTemplateClick({ ...card, productType: group.productType })}
                            >
                              Select
                            </ReactEDSWrapper>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewAutomationFlow;
